import cloneDeep from 'lodash/cloneDeep';

import {ActionTree, MutationTree} from 'vuex';
import {RootState} from '@/store/types';
import {ServerResult} from '@/core/http/server_result';
import {fetchEnumsFromSchema} from '@/core/tools/forms/fetch_enums_from_schema';
import {generateFormObject} from '@/core/tools/forms/generate_form_object';
import {prepareFormObjectForSave} from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface BasesManagerFormState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  document: any;
  form: any;
  prescript: any;
  prescript_schema: any;
  schema: any;
  products: any[];
  ingredients: any[];
  ingredients_schema: any;

  targetForDelete: any[];

  meta: any;
  prescript_meta: any;
}

const state: BasesManagerFormState = {
  loading: false,
  loaded: false,
  fatalError: false,

  document: null,
  form: null,
  prescript: null,
  schema: null,
  ingredients_schema: null,
  prescript_schema: null,

  meta: {},
  prescript_meta: {},
  products: [],
  ingredients: [],
  targetForDelete: []
};

const initState: BasesManagerFormState = cloneDeep(state);

export const BASES_MANAGER_DATA = 'GET_BASES_MANAGER_DATA';
export const BASES_PRODUCTS_DATA = 'GET_BASES_PRODUCTS_DATA';
export const BASES_PRESCRIPT_DATA = 'GET_BASES_PRESCRIPT_DATA';
export const BASES_PRESCRIPT_INGREDIENTS_DATA = 'BASES_PRESCRIPT_INGREDIENTS_DATA';
export const BASES_MANAGER_SCHEMA = 'GET_BASES_MANAGER_SCHEMA';
export const BASES_PRESCRIPT_SCHEMA = 'GET_BASES_PRESCRIPT_SCHEMA';
export const BASES_PRESCRIPT_INGREDIENTS_SCHEMA = 'GET_PRESCRIPT_INGREDIENTS_SCHEMA';

export const actions: ActionTree<BasesManagerFormState, RootState> = {
  reset({commit}) {
    commit('RESET_STATE')
  },

  init({commit, dispatch}, params) {
    return dispatch('loadData', params);
  },

  loadData({commit}, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      vm.$db([
        vm.$q(BASES_MANAGER_DATA)
          .raw('documents.get')
          .action({
            id: params.id
          }),
        vm.$q(BASES_PRODUCTS_DATA)
          .raw('documents.list')
          .action({
            columns: {
              id: 'vbp.id',
              base_id: 'vbp.base_id',
              product_id: 'vbp.product_id'
            },
            from: `s:voc_base_products`,
            from_as: 'vbp',
            where: {
              $eq: [`vbp.base_id`, {$: params.id}]
            }
          }),
        vm.$q(BASES_PRESCRIPT_DATA)
          .raw('documents.list')
          .action({
            columns: {
              id: 'p.id',
              name: 'p.name',
              total_weight: 'p.total_weight',
              datetime: 'p.datetime',
              protein: 'p.protein',
              fats: 'p.fats',
              carbohydrate: 'p.carbohydrate',
              calories: 'p.calories',
              energy: 'p.energy',
              water: 'p.water',
              water_percent: 'p.water_percent',
              archived_at: 'p.archived_at',
              created_at: 'p.created_at',
              updated_at: 'p.updated_at',
              base_id: 'p.base_id',
              copy_id: 'p.copy_id',
              close_date: 'p.close_date',
              type: 'p.type',
              water_borders: 'p.water_borders',
              plus_water: 'p.plus_water',
            },
            from: `s:prescripts`,
            from_as: 'p',
            where: {
              $and: [
                {$eq: [`p.base_id`, {$: params.id}]},
                {$null: ['p.archived_at']},
              ]
            }
          }),
        vm.$q(BASES_PRESCRIPT_INGREDIENTS_DATA)
          .raw('documents.list')
          .action({
            "columns": {
              "id": "ppi.id",
              "prescript_id": "ppi.prescript_id",
              "ingredient_id": "ppi.ingredient_id",
              "weight": "ppi.weight",
              "percent": "ppi.percent",
              "protein": "ppi.protein",
              "fats": "ppi.fats",
              "carbohydrate": "ppi.carbohydrate",
              "protein_percent": "vi.protein",
              "fats_percent": "vi.fats",
              "carbohydrate_percent": "vi.carbohydrate",
              "position": "ppi.position",
            },
            "from": "s:prescripts_ingredients",
            "from_as": "ppi",
            "joins": [
              {
                join: 's:voc_ingredients',
                type: 'left',
                as: 'vi',
                on: {
                  $and: [
                    {$eq: ['vi.id', 'ppi.ingredient_id']},
                    {$eq: ['vi.is_vdp', {$: true}]}
                  ]
                }
              },
              {
                join: 's:prescripts',
                // type: 'left',
                as: 'p',
                on: {
                  $and: [
                    {$eq: ['p.id', 'ppi.prescript_id']},
                    {$null: ['p.archived_at']},
                  ]
                }
              }
            ],
            order: [
              {$asc: ['ppi.position']}
            ],
            "where": {"$eq": ['p.base_id', {$: params.id}]}
          }),
        vm.$q(BASES_PRESCRIPT_INGREDIENTS_SCHEMA)
          .raw('schemas.get')
          .action({
            id: `s:prescripts_ingredients`
          }),
      ]).then((result: ServerResult) => {
        commit('SET_FORM_DATA', {
          bases: result.getOperation(BASES_MANAGER_DATA).getContent(),
          products: result.getOperation(BASES_PRODUCTS_DATA).getContent(),
          prescripts: result.getOperation(BASES_PRESCRIPT_DATA).getContent(),
          ingredients: result.getOperation(BASES_PRESCRIPT_INGREDIENTS_DATA).getContent(),
          ingredients_schema: result.getOperation(BASES_PRESCRIPT_INGREDIENTS_SCHEMA).getContent().schema
        });

        commit('LOADED_SUCCESS');
        resolve(null);
      })
        .catch((error: any) => {
          console.log('error', error)
          commit('LOADED_ERROR');
          reject()
        })
    })
  },

  setPositionIngredient({commit}, {oldIndex, newIndex}){
    commit('SET_POSITION', {oldIndex, newIndex})
  },

  addItem({state}, {base_id}) {
    state.products.push({base_id: base_id})
  },

  removeItem({state}, index: number) {
    if (state.products[index].id) {
      state.targetForDelete.push(state.products[index].id)
    }

    state.products.splice(index, 1);
  },

  prepareNew({commit, state}, initParams) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      vm.$db([
        vm.$q(BASES_MANAGER_SCHEMA)
          .raw('schemas.get')
          .action({
            id: `s:voc_bases`
          }),
        vm.$q(BASES_PRESCRIPT_SCHEMA)
          .raw('schemas.get')
          .action({
            id: `s:prescripts`
          }),
        vm.$q(BASES_PRESCRIPT_INGREDIENTS_SCHEMA)
          .raw('schemas.get')
          .action({
            id: `s:prescripts_ingredients`
          }),
      ]).then((result: ServerResult) => {
        commit('GENERATE_FORM_DATA', {
          bases: result.getOperation(BASES_MANAGER_SCHEMA).getContent(),
          prescript_schema: result.getOperation(BASES_PRESCRIPT_SCHEMA).getContent().schema,
          ingredients_schema: result.getOperation(BASES_PRESCRIPT_INGREDIENTS_SCHEMA).getContent().schema,
          initParams
        });

        commit('LOADED_SUCCESS');
        resolve();
      })
        .catch((error: any) => {
          console.log('error', error)
          commit('LOADED_ERROR');
          reject()
        })
    })
  },

  saveForm({commit}, documentId) {
    const vm = (this as any)._vm;

    const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      let saveQuery = null;
      const normalizedItemsData = state.products.map((item, index) => {
        return Object.keys(item)
          .reduce((obj: any, key: string) => {
            if (key == 'id') {
              item[key] = (typeof (item[key]) == 'string') ? item[key] : null;
            }
            obj[key] = item[key];
            return obj;
          }, {})
      });

      if (state.form.id) {
        saveQuery = vm.$q('BASES_MANAGER_DATA_SAVE')
          .update(state.form.id, normalizedForm)
          .createManyRelation('voc_base_products', 'base_id', (createRelated: any) => {
            return normalizedItemsData.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
          })
          .updateManyRelation('voc_base_products', 'base_id', (updateRelated: any) => {
            return normalizedItemsData.filter((item: any) => (typeof (item.id) == 'string')).map((item, index) => updateRelated(item))
          })
          .deleteManyRelation('voc_base_products', 'base_id', (deleteRelated: any) => {
            return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
          })
      } else {
        saveQuery = vm.$q('BASES_MANAGER_DATA_SAVE')
          .create('s:voc_bases', normalizedForm)
          .withManyRelation('voc_base_products', 'base_id', (createRelated: any) => {
            return normalizedItemsData.map((item: any) => createRelated(item));
          })
      }

      const queries = [
        saveQuery
      ]

      vm.$db(queries).then((result: ServerResult) => {
        const operationResult = result.getOperation('BASES_MANAGER_DATA_SAVE');
        commit('CHANGE_PRESCRIPT_VALUE', {field: 'weight', newValue: state.prescript.weight});

        if (operationResult.hasError()) {
          console.log('error', operationResult.getContent());

          commit('LOADED_SUCCESS');
          reject();
        } else {
          const ingredients = state.ingredients.map((item: any) => {
            return prepareFormObjectForSave(state.ingredients_schema, item)
          })
          console.log('operationResult.getContent() => ', operationResult.getContent());

          const normalizedForm = prepareFormObjectForSave(state.prescript_schema, state.prescript);
          normalizedForm.base_id = operationResult.getContent().document.id;

          // @ts-ignore
          const prescriptId = state.prescript.id;

          if (prescriptId) {
            saveQuery = vm.$q('PRESCRIPT_MANAGER_DATA_SAVE')
              .update(prescriptId, normalizedForm)
              .createManyRelation('prescripts_ingredients', 'prescript_id', (createRelated: any) => {
                return ingredients.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
              })
              .updateManyRelation('prescripts_ingredients', 'prescript_id', (updateRelated: any) => {
                return ingredients.filter((item: any) => (typeof (item.id) == 'string')).map((item, index) => updateRelated(item))
              })
              .deleteManyRelation('prescripts_ingredients', 'prescript_id', (deleteRelated: any) => {
                return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
              })

          } else {
            saveQuery = vm.$q('PRESCRIPT_MANAGER_DATA_SAVE')
              .create('s:prescripts', normalizedForm)
              .withManyRelation('prescripts_ingredients', 'prescript_id', (createRelated: any) => {
                return ingredients.map((item: any) => createRelated(item));
              })
          }
          vm.$db([
            saveQuery
          ]).then((result: ServerResult) => {
            const operationResult = result.getOperation('PRESCRIPT_MANAGER_DATA_SAVE');

            if (operationResult.hasError()) {
              console.log('error', operationResult.getContent());

              commit('LOADED_SUCCESS');
              reject();
            } else {
              commit('LOADED_SUCCESS');
              resolve(null);
            }
          })
            .catch((error: any) => {
              console.error('errror', error)
              reject(error);
              commit('LOADED_SUCCESS');
            });
        }
      })
        .catch((error: any) => {
          console.error('errror', error)
          reject(error);
        });
    })
  },

  changeFormValue({commit}, {field, newValue}) {
    commit('CHANGE_VALUE', {field, newValue});
  },

  changeProductValue({state}, {index, field, newValue}) {
    state.products[index][field] = newValue;
  },

  changeIngredientValue({commit}, {idx, field, newValue}) {
    commit('CHANGE_INGREDIENT_VALUE', {idx, field, newValue});
  },

  changePrescriptValue({commit, state}, {field, newValue}) {
    commit('CHANGE_PRESCRIPT_VALUE', {field, newValue});
    state.ingredients.map((item: any, index: any) => {
      commit('CHANGE_INGREDIENT_VALUE', {idx: index, field: 'weight', newValue: item.weight});
    })
  },

  changePrescriptWaterBorderValue({commit}, {idx, field, newValue}) {
    commit('CHANGE_PRESCRIPT_WATER_BORDER_VALUE', {idx, field, newValue});
  },

  addPrescriptWaterBorder({commit}) {
    commit('ADD_PRESCRIPT_WATER_BORDER')
  },

  removePrescriptWaterBorder({commit}, {index}) {
    commit('DEL_PRESCRIPT_WATER_BORDER', {index})
  },

  addIngredient({commit}) {
    commit('ADD_INGREDIENT')
  },

  copyIngredient({commit}, {idx}) {
    commit('COPY_INGREDIENT', {idx})
  },


  delIngredient({commit}, {idx}) {
    commit('DEL_INGREDIENT', {idx})
  },

};

export const mutations: MutationTree<BasesManagerFormState> = {
  RESET_STATE: (state) => {
    for (const key in initState) {
      (state as any)[key] = (initState as any)[key]
    }
  },

  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },

  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },

  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_FORM_DATA(state, {bases, products, prescripts, ingredients, ingredients_schema}) {
    state.document = generateFormObject(bases.schema, bases.document);
    if (!state.document.tooltip_41) {
      state.document.tooltip_41 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_42) {
      state.document.tooltip_42 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_43) {
      state.document.tooltip_43 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_44) {
      state.document.tooltip_44 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_71) {
      state.document.tooltip_71 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_72) {
      state.document.tooltip_72 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_73) {
      state.document.tooltip_73 = {data: [5, 6.5]}
    }
    if (!state.document.tooltip_74) {
      state.document.tooltip_74 = {data: [5, 6.5]}
    }
    if (!state.document.temp_border_ok) {
      state.document.temp_border_ok = {data: [34, 38]}
    }
    state.schema = bases.schema;
    state.meta = fetchEnumsFromSchema(bases.schema);

    state.form = generateFormObject(bases.schema, state.document);
    state.products = products.documents;

    state.prescript_schema = prescripts.schema
    state.prescript = generateFormObject(state.prescript_schema, prescripts.documents.length ? prescripts.documents[0] : {type: 'g', water_borders: {borders: []}});
    state.prescript_meta = fetchEnumsFromSchema(prescripts.schema);

    state.ingredients = ingredients.documents.map((item: any, idx: number) => {
      if (!item.position) {
        item.position = idx + 1;
      }
      return item
    })
    state.ingredients_schema = ingredients_schema

    console.log('state.ingredients => ', state.ingredients);
  },

  GENERATE_FORM_DATA(state, {bases, prescript_schema, ingredients_schema}) {
    state.document = {};
    state.products = []
    state.document.tooltip_41 = {data: [5, 6.5]}
    state.document.tooltip_42 = {data: [5, 6.5]}
    state.document.tooltip_43 = {data: [5, 6.5]}
    state.document.tooltip_44 = {data: [5, 6.5]}
    state.document.tooltip_71 = {data: [5, 6.5]}
    state.document.tooltip_72 = {data: [5, 6.5]}
    state.document.tooltip_73 = {data: [5, 6.5]}
    state.document.tooltip_74 = {data: [5, 6.5]}
    state.document.temp_border_ok = {data: [34, 38]}
    state.schema = bases.schema;
    state.meta = fetchEnumsFromSchema(bases.schema);

    state.form = generateFormObject(bases.schema, {});
    // Для новой рецептуры тип продукта по умолчанию — «Системный» (products_types id=8)
    state.form.product_type_id = 'd:products_types:8';
    state.prescript_schema = prescript_schema
    state.prescript = generateFormObject(prescript_schema, {type: 'g', water_borders: {borders: []}});
    state.prescript_meta = fetchEnumsFromSchema(prescript_schema);

    state.ingredients = []
    state.ingredients_schema = ingredients_schema
  },

  ON_FORM_SAVE(state, {document, schema}) {
    state.document = generateFormObject(schema, document);
    state.schema = schema;

    state.form = generateFormObject(schema, state.document);
  },

  CHANGE_VALUE(state: BasesManagerFormState, {field, newValue}) {
    switch (field) {
      case 'ferment_delay':
      case 'done_delay':
        newValue = moment.duration(moment(newValue).utc().local().format('HH:mm:ss')).asSeconds()
        state.form[field] = {'months': 0, 'days': 0, 'secs': newValue};
        break;
      default:
        state.form[field] = newValue;
    }
  },

  CHANGE_PRESCRIPT_VALUE(state: BasesManagerFormState, {field, newValue}) {
    switch (field) {
      default:
        state.prescript[field] = newValue;
    }
  },

  CHANGE_INGREDIENT_VALUE(state: BasesManagerFormState, {idx, field, newValue}) {
    const calc_water: any = () => {
      state.prescript.water = state.prescript.total_weight - state.ingredients.reduce((acc: number, item: any) => {
        if (!item.is_composite) {
          acc += item.weight;
        }
        return acc
      }, 0)
      state.prescript.water_percent = Math.round(state.prescript.water / state.prescript.total_weight * 100 * 10000) / 10000

      state.prescript.protein = 0
      state.prescript.fats = 0
      state.prescript.carbohydrate = 0

      const _data = state.ingredients.reduce((acc: any, item: any) => {
        acc.protein += item.protein
        acc.fats += item.fats
        acc.carbohydrate += item.carbohydrate
        return acc
      }, {protein: 0, fats: 0, carbohydrate: 0})

      state.prescript.protein = Math.round(_data.protein * 10000) / 10000;
      state.prescript.fats = Math.round(_data.fats * 10000) / 10000;
      state.prescript.carbohydrate = Math.round(_data.carbohydrate * 10000) / 10000;
      state.prescript.calories = Math.round((
        _data.protein / 1 * 4 +
        _data.fats / 1 * 9 +
        _data.carbohydrate / 1 * 4
      ) * 10000) / 10000;
      state.prescript.energy = Math.round(state.prescript.calories * 4.19 * 10000) / 10000;
    }
    switch (field) {
      case 'weight':
        state.ingredients[idx].weight = newValue;
        state.ingredients[idx].percent = Math.round(newValue / state.prescript.total_weight * 100 * 10000) / 10000;
        calc_water()
        break
      case 'percent':
        state.ingredients[idx].percent = newValue;
        state.ingredients[idx].weight = Math.round(state.prescript.total_weight / 100 * newValue * 10000) / 10000;
        calc_water()
        break
      default:
        state.ingredients[idx][field] = newValue;
    }
    // @ts-ignore
    state.ingredients[idx].protein = state.ingredients[idx].weight * state.ingredients[idx].protein_percent / state.prescript.total_weight;
    // @ts-ignore
    state.ingredients[idx].fats = state.ingredients[idx].weight * state.ingredients[idx].fats_percent / state.prescript.total_weight;
    // @ts-ignore
    state.ingredients[idx].carbohydrate = state.ingredients[idx].weight * state.ingredients[idx].carbohydrate_percent / state.prescript.total_weight;
  },

  CHANGE_PRESCRIPT_WATER_BORDER_VALUE(state: BasesManagerFormState, {idx, field, newValue}) {
    switch (field) {
      case 'temp':
      case 'coef':
        state.prescript.water_borders.borders[idx][field] = newValue
        state.prescript.water_borders.borders[idx].water = Math.round((state.prescript.water / state.prescript.water_borders.borders[idx].coef + state.prescript.plus_water ) / 1000)
        break

      default:
        state.prescript.water_borders.borders[idx][field] = newValue
    }

  },

  ADD_PRESCRIPT_WATER_BORDER(state: BasesManagerFormState) {
    state.prescript.water_borders.borders.push({
      temp: 0,
      coef: 0,
      water: 0
    })
  },

  DEL_PRESCRIPT_WATER_BORDER(state: BasesManagerFormState, {idx}) {
    state.prescript.water_borders.borders.splice(idx, 1)
  },

  ADD_INGREDIENT(state: BasesManagerFormState) {
    state.ingredients.push({
      ingredient_id: null,
      weight: 0,
      percent: 0
    })
  },

  COPY_INGREDIENT(state: BasesManagerFormState, {idx}) {
    state.ingredients.push({...state.ingredients[idx]})
  },

  DEL_INGREDIENT(state: BasesManagerFormState, {idx}) {
    if (state.ingredients[idx].id) {
      state.targetForDelete.push(state.ingredients[idx].id)
    }
    state.ingredients.splice(idx, 1)
  },

  SET_POSITION(state: BasesManagerFormState, {oldIndex, newIndex}) {
    console.log(oldIndex, newIndex)
    const oldPosition = state.ingredients[oldIndex].position + 0
    const newPosition = state.ingredients[newIndex].position + 0 
    state.ingredients[oldIndex].position = newPosition
    state.ingredients[newIndex].position = oldPosition
    state.ingredients.sort((a: any, b: any) => {
      if (a['position'] > b['position']) return 1
      if (a['position'] < b['position']) return -1
      return 0
    })
  }
};

const getters = {
  fields: (state: BasesManagerFormState) => {
    return state.schema.fields.reduce((prev: any, field: any) => {
      prev[field.code] = field;
      return prev;
    }, {});
  },
};

export const STORE_KEY = '$_bases_manager_form';

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
